import type { Tables } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

/**
 * Relatório do paciente — rascunho gerado por IA, aprovado pelo médico.
 *
 * O texto nasce de `patient-report` (edge function) a partir do MESMO brief
 * que originou a análise técnica. Aqui o painel só lê, edita e aprova: nenhum
 * número é recalculado no cliente, pelo mesmo motivo de sempre — duas contas
 * para o mesmo período acabam divergindo, e a que aparecer primeiro ganha.
 */

export type PatientReport = Tables<'patient_reports'>;

/** O que o modelo devolve e o médico edita. */
export interface ReportContent {
  saudacao: string;
  resumo: string;
  destaques: {
    titulo: string;
    texto: string;
    direcao: 'melhora' | 'piora' | 'estavel' | 'atencao';
  }[];
  habitos: string[];
  proxima_conversa: string[];
  fechamento: string;
}

/** Campo do relatório que citou número sem respaldo no brief. */
export interface TextoSuspeito {
  campo: string;
  numeros_sem_respaldo: number[];
}

export function useReports(patientId: string) {
  return useQuery({
    queryKey: ['patient-reports', patientId],
    queryFn: async (): Promise<PatientReport[]> => {
      const { data, error } = await supabase
        .from('patient_reports')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Pede o rascunho à edge function.
 *
 * Recebe `analysisId` e não `patientId`: o relatório é derivado de uma análise
 * específica, e deixar o servidor escolher "a mais recente" criaria uma corrida
 * — o médico lê uma análise na tela, clica em gerar, e recebe um texto escrito
 * sobre outra que chegou nesse meio-tempo.
 */
export function useGenerateReport(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (analysisId: string) => {
      const { data, error } = await supabase.functions.invoke('patient-report', {
        body: { analysisId },
      });

      if (error) {
        const detalhe = await (error as { context?: Response }).context
          ?.json()
          .catch(() => null);
        throw new Error(
          detalhe?.message ?? detalhe?.error ?? 'Não foi possível gerar o relatório.',
        );
      }
      return data as {
        status: string;
        reportId: string;
        unverified: TextoSuspeito[];
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patient-reports', patientId] });
    },
  });
}

/** Salva a edição do médico. Não aprova — são ações separadas de propósito. */
export function useSaveReport(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      reportId: string;
      content: ReportContent;
      doctorMessage: string;
    }) => {
      const { error } = await supabase
        .from('patient_reports')
        .update({
          content: args.content as unknown as never,
          doctor_message: args.doctorMessage || null,
        })
        .eq('id', args.reportId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patient-reports', patientId] });
    },
  });
}

/**
 * O PORTÃO. Só depois disto o documento pode ser impresso ou enviado.
 *
 * Separado de `useSaveReport` porque salvar rascunho e assumir o texto como
 * seu são decisões diferentes: o médico pode querer guardar no meio da edição
 * sem ainda concordar com o conteúdo.
 */
export function useApproveReport(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: string) => {
      const { data: sessao } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('patient_reports')
        .update({
          approved_at: new Date().toISOString(),
          approved_by: sessao.user?.id ?? null,
        })
        .eq('id', reportId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patient-reports', patientId] });
    },
  });
}
